import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { generateCustomerHistoryTemplate } from '../../utils/excelTemplateGenerator';
import customerHistoryService from '../../services/customerHistoryService';
import useToast from '../../hooks/useToast';
import LoadingSpinner from '../ui/LoadingSpinner';

interface BulkUploadModalProps {
  customerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

// Helper function to format error messages for display
const formatErrorMessage = (error: Error | string | unknown): string => {
  let errorMessage: string;
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = 'Unknown error occurred';
  }
  
  const lowerErrorMessage = errorMessage.toLowerCase();
  
  // Check if error is about missing columns (even if prefixed with "Error processing file:")
  if (lowerErrorMessage.includes('missing required columns') || 
      lowerErrorMessage.includes('missing columns') ||
      (lowerErrorMessage.includes('required columns') && 
       (lowerErrorMessage.includes('invoice') || lowerErrorMessage.includes('quote') || 
        lowerErrorMessage.includes('store type') || lowerErrorMessage.includes('created date') || 
        lowerErrorMessage.includes('grand total')))) {
    return 'Invalid File: Required columns are missing. Please ensure that the file includes all mandatory fields listed in the upload guidelines.';
  }
  
  // Check if error is about unsupported file format
  if (lowerErrorMessage.includes('unsupported file format') ||
      lowerErrorMessage.includes('unsupported file')) {
    return 'Invalid file format.\n\nPlease upload an XLSX file (.xlsx).';
  }
  
  // If error starts with "Error processing file:" but we haven't matched a specific pattern,
  // try to extract a more meaningful message or return a generic one
  if (lowerErrorMessage.startsWith('error processing file:')) {
    // Check if it's a column-related error that we might have missed
    if (lowerErrorMessage.includes('column') || lowerErrorMessage.includes('invoice') || 
        lowerErrorMessage.includes('quote') || lowerErrorMessage.includes('store type')) {
      return 'Invalid File: Required columns are missing. Please ensure that the file includes all mandatory fields listed in the upload guidelines.';
    }
    // Otherwise, return a generic message without the technical prefix
    const cleanedMessage = errorMessage.replace(/^Error processing file:\s*/i, '').trim();
    return cleanedMessage || 'Invalid File: Required columns are missing. Please ensure that the file includes all mandatory fields listed in the upload guidelines.';
  }
  
  // Return the original error message if no specific pattern matches
  return errorMessage;
};

// Styled components - Updated to match inventory modal styling
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 32px;
  border-radius: 12px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const SubmittingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
`;

const SubmittingText = styled.p`
  margin-top: 1rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #042B41;
  margin: 0 0 24px 0;
  text-align: center;
`;

const UploadArea = styled.div`
  border: 2px dashed #6b7280;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  margin-bottom: 24px;
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;
  background-color: #f9fafb;

  &:hover {
    border-color: #059669;
    background-color: #f0fdf4;
  }
`;

const UploadText = styled.p`
  color: #6b7280;
  margin: 8px 0;
  font-size: 16px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileName = styled.p`
  color: #059669;
  font-weight: 500;
  margin-top: 16px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 14px;

  &.primary {
    background-color: #6b7d8c;
    color: white;
    border: none;

    &:hover {
      background-color: #5a6c79;
    }

    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }

    &.file-selected {
      background-color: #0f172a;
      color: white;

      &:hover {
        background-color: #1e293b;
      }
    }
  }

  &.secondary {
    background-color: white;
    color: #4b5563;
    border: 1px solid #d1d5db;

    &:hover {
      background-color: #f9fafb;
    }
  }
`;

const DownloadTemplateLink = styled.a`
  color: #059669;
  text-decoration: none;
  display: block;
  margin-top: 16px;
  cursor: pointer;
  font-size: 14px;
  text-align: center;

  &:hover {
    text-decoration: underline;
    color: #047857;
  }
`;

const ResultContainer = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
`;

const ResultTitle = styled.h3`
  color: #042B41;
  margin-top: 0;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.p`
  color: #059669;
  font-weight: 500;
`;

const ErrorItem = styled.p`
  color: #dc2626;
  margin: 4px 0;
`;

const ErrorContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: 12px;
  padding: 8px;
  background-color: #fee2e2;
  border-radius: 4px;
`;

const InfoText = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: #e0f2fe;
  border-radius: 8px;
  font-size: 14px;
  border-left: 4px solid #0891b2;

  p {
    margin: 8px 0;
  }

  strong {
    color: #042B41;
  }
`;

// Validation Error Alert Modal Styled Components
const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
`;

const AlertModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 480px) {
    padding: 1rem;
    max-width: 100%;
  }
`;

const AlertModalTitle = styled.h2`
  font-size: 1.125rem;
  color: #DC2626;
  margin: 0 0 0.75rem 0;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const AlertModalMessage = styled.div`
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;
  white-space: pre-line;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const AlertButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.75rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AlertCancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 36px;
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.625rem;
    min-height: 36px;
  }
`;

const CustomerHistoryBulkUploadModal: React.FC<BulkUploadModalProps> = ({ customerId, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success_count: number; error_count: number; errors: string[] } | null>(null);
  const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      
      // Check if file is .xlsx
      if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
        toast.showToast('Please select only .xlsx files', { type: 'error' });
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setLoading(true);
      console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      const result = await customerHistoryService.bulkUploadHistory(customerId, file);
      setResult(result);
      
      if (result.success_count > 0 && result.error_count === 0) {
        toast.showToast(`Successfully imported ${result.success_count} history items`, { type: 'success' });
        onSuccess(); // Close modal immediately and refresh data
      } else if (result.success_count > 0) {
        toast.showToast(`Imported ${result.success_count} items with ${result.error_count} errors`, { type: 'info' });
        // Still refresh data if some items were imported successfully
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        toast.showToast('Import failed. Please check the errors and try again.', { type: 'error' });
        // Don't close modal on complete failure, let user see errors
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show validation error modal instead of just toast
      const formattedMessage = formatErrorMessage(error);
      setValidationErrorMessage(formattedMessage);
      setShowValidationErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseValidationErrorModal = () => {
    setShowValidationErrorModal(false);
    setValidationErrorMessage('');
  };

  const downloadTemplate = async () => {
    try {
      await generateCustomerHistoryTemplate();
      toast.showToast('Template downloaded successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.showToast('Failed to download template. Please try again.', { type: 'error' });
    }
  };

  return (
    <Modal>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {loading && (
          <SubmittingOverlay>
            <LoadingSpinner size="lg" />
            <SubmittingText>Uploading customer history...</SubmittingText>
          </SubmittingOverlay>
        )}
        <Title>Bulk Upload Customer History</Title>
        
        <UploadArea onClick={handleUploadClick}>
          <UploadText>Click or drag file to this area to upload</UploadText>
          <UploadText>Support for XLSX files</UploadText>
          <FileInput 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx"
          />
          {file && <FileName>{file.name}</FileName>}
        </UploadArea>
        
        <ButtonGroup>
          <Button className="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className={`primary ${file ? 'file-selected' : ''}`}
            onClick={handleUpload}
            disabled={!file || loading}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </ButtonGroup>
        
        <DownloadTemplateLink onClick={downloadTemplate}>
          Download Excel Template
        </DownloadTemplateLink>
        
        {result && (
          <ResultContainer>
            <ResultTitle>Upload Results</ResultTitle>
            {result.success_count > 0 && (
              <SuccessMessage>Successfully imported {result.success_count} history items</SuccessMessage>
            )}
            
            {result.error_count > 0 && (
              <>
                <ErrorItem>Failed to import {result.error_count} items</ErrorItem>
                <ErrorContainer>
                  {result.errors.map((error, index) => (
                    <ErrorItem key={index}>{error}</ErrorItem>
                  ))}
                </ErrorContainer>
              </>
            )}
          </ResultContainer>
        )}
        
        <InfoText>
          <p><strong>Required Fields:</strong> Invoice/Quote Number, Store Type, Created Date, Grand Total (AUD)</p>
          <p><strong>Optional Fields:</strong> Type (Enter "Quote/Invoice")</p>
          <p><strong>Date Format:</strong> DD/MM/YYYY (e.g., 27/06/2025)</p>
          <p><strong>File Format:</strong> Use the Excel template (.xlsx) for best experience.</p>
        </InfoText>
      </ModalContent>

      {/* Validation Error Alert Modal */}
      {showValidationErrorModal && (
        <AlertModalOverlay onClick={handleCloseValidationErrorModal}>
          <AlertModalContent onClick={(e) => e.stopPropagation()}>
            <AlertModalTitle>File Validation Error</AlertModalTitle>
            <AlertModalMessage>{validationErrorMessage}</AlertModalMessage>
            <AlertButtonContainer>
              <AlertCancelButton onClick={handleCloseValidationErrorModal}>
                Close
              </AlertCancelButton>
            </AlertButtonContainer>
          </AlertModalContent>
        </AlertModalOverlay>
      )}
    </Modal>
  );
};

export default CustomerHistoryBulkUploadModal; 